import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import Layout from '@/components/Layout';
import {
  accept, complete, decline, getReferral, markNoShow, uploadResult, type ReferralDetail,
} from '@/lib/api';

type Busy = null | 'accept' | 'decline' | 'noshow' | 'complete' | 'upload';

const ReferralDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [referral, setReferral] = useState<ReferralDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [report, setReport] = useState('');
  const [progress, setProgress] = useState<string | null>(null);

  const load = useCallback(() => {
    getReferral(id)
      .then((r) => { setReferral(r); setReport(r.centreReport ?? ''); })
      .catch(() => setError(t('detail.loadError', 'This referral is not available.')));
  }, [id, t]);

  useEffect(load, [load]);

  const act = async (kind: Busy, fn: () => Promise<unknown>) => {
    setBusy(kind); setError(null);
    try { await fn(); load(); }
    catch (e: any) { setError(e?.response?.data?.title ?? t('detail.actionFailed', 'That did not work.')); }
    finally { setBusy(null); }
  };

  /** Files go up one at a time, each with its own freshly-minted URL. */
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy('upload'); setError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(t('detail.uploading', 'Uploading {{n}} of {{total}}…', { n: i + 1, total: files.length }));
        await uploadResult(id, files[i]);
      }
      load();
    } catch (e: any) {
      setError(e?.response?.data?.title ?? t('detail.uploadFailed', 'The upload did not finish.'));
    } finally { setBusy(null); setProgress(null); }
  };

  if (error && !referral) return <Layout><Alert variant="error">{error}</Alert></Layout>;
  if (!referral) return <Layout><div className="flex justify-center py-10"><Spinner size="lg" /></div></Layout>;

  const live = referral.status === 'Requested' || referral.status === 'Accepted';
  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  const delivered = referral.files.filter((f) => f.sizeBytes != null).length;

  return (
    <Layout>
      <button onClick={() => navigate('/')} className="mb-3 text-sm text-muted hover:text-body-strong">
        ← {t('detail.back', 'Back to referrals')}
      </button>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Who is coming in — the only identity the clinic shares, and all reception needs. */}
        <Card>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-subtle-ink">
            {t('detail.patient', 'Patient')}
          </h2>
          <div className="text-lg font-semibold text-body">{referral.patientDisplayName}</div>
          {referral.patientDateOfBirth && (
            <div className="text-sm text-muted">{date(referral.patientDateOfBirth)}</div>
          )}
          <a href={`tel:${referral.patientPhone}`} className="mt-1 inline-block text-sm font-medium text-brand-ink">
            {referral.patientPhone}
          </a>
          <div className="mt-3 border-t border-line-soft pt-3 text-xs text-muted">
            {t('detail.from', 'Referred by {{doctor}} · {{clinic}}', {
              doctor: referral.referringDoctorName, clinic: referral.clinicName,
            })}
            <div className="mt-1">{t('detail.raised', 'Raised {{date}}', { date: date(referral.createdAt) })}</div>
          </div>
        </Card>

        {/* What to do, and why — the justification a radiographer needs before exposing anyone. */}
        <Card>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-subtle-ink">
            {t('detail.requested', 'Requested')}
          </h2>
          <ul className="mb-3 space-y-1">
            {referral.studies.map((s, i) => (
              <li key={i} className="text-sm text-body">
                <span className="font-medium">{t(`modality.${s.modality}`, s.modality)} ×{s.quantity}</span>
                {s.toothNumbers && <span className="text-muted"> · {s.toothNumbers}</span>}
                {s.note && <div className="text-xs text-muted">{s.note}</div>}
              </li>
            ))}
          </ul>
          <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-900">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
              {t('detail.reason', 'Clinical reason')}
            </div>
            {referral.clinicalReason}
          </div>
          <div className="mt-3 text-sm">
            <span className="text-muted">{t('detail.due', 'Due by')} </span>
            <span className="font-semibold text-body">{date(referral.dueDate)}</span>
            {referral.isUrgent && (
              <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
                {t('worklist.urgent', 'Urgent')}
              </span>
            )}
          </div>
        </Card>
      </div>

      {/* Delivery. Files only count once the transfer is confirmed. */}
      <Card className="mt-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-subtle-ink">
          {t('detail.images', 'Images')}
        </h2>

        {referral.files.length === 0 ? (
          <p className="text-sm text-muted">{t('detail.noImages', 'Nothing uploaded yet.')}</p>
        ) : (
          <ul className="mb-3 divide-y divide-line-soft">
            {referral.files.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-body-strong">{f.fileName}</span>
                <span className="text-xs text-subtle-ink">
                  {f.sizeBytes == null
                    ? t('detail.incomplete', 'incomplete')
                    : `${(f.sizeBytes / 1_000_000).toFixed(1)} MB`}
                </span>
              </li>
            ))}
          </ul>
        )}

        {live && (
          <>
            <input
              ref={fileInput} type="file" multiple className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
            <Button variant="secondary" loading={busy === 'upload'} onClick={() => fileInput.current?.click()}>
              {t('detail.addImages', 'Add images')}
            </Button>
            {progress && <span className="ml-2 text-xs text-muted">{progress}</span>}
          </>
        )}
      </Card>

      {/* The four outcomes. Only what is possible from the current state is offered. */}
      {live && (
        <Card className="mt-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-subtle-ink">
            {t('detail.outcome', 'Outcome')}
          </h2>

          {referral.status === 'Requested' && (
            <div className="flex flex-wrap gap-2">
              <Button loading={busy === 'accept'} onClick={() => act('accept', () => accept(id))}>
                {t('detail.accept', 'Accept')}
              </Button>
              <Button variant="ghost" onClick={() => setShowDecline((v) => !v)}>
                {t('detail.decline', 'Decline')}
              </Button>
            </div>
          )}

          {referral.status === 'Accepted' && (
            <>
              <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
                {t('detail.report', 'Findings (optional)')}
              </label>
              <textarea
                value={report} onChange={(e) => setReport(e.target.value)} rows={3}
                className="mb-3 w-full rounded-lg border border-line-strong p-2 text-sm"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  loading={busy === 'complete'}
                  disabled={delivered === 0}
                  onClick={() => act('complete', () => complete(id, report.trim() || null))}
                >
                  {t('detail.complete', 'Send to clinic')}
                </Button>
                <Button variant="ghost" loading={busy === 'noshow'} onClick={() => act('noshow', () => markNoShow(id))}>
                  {t('detail.noShow', 'Patient did not arrive')}
                </Button>
              </div>
              {delivered === 0 && (
                <p className="mt-2 text-xs text-muted">
                  {t('detail.needImage', 'Upload at least one image before sending it to the clinic.')}
                </p>
              )}
            </>
          )}

          {showDecline && (
            <div className="mt-3 border-t border-line-soft pt-3">
              <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
                {t('detail.declineReason', 'Why can you not take it?')}
              </label>
              <input
                value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                placeholder={t('detail.declinePlaceholder', 'e.g. CBCT unavailable this week')}
                className="mb-2 w-full rounded-lg border border-line-strong px-3 py-2 text-sm"
              />
              <Button
                variant="secondary" loading={busy === 'decline'} disabled={!declineReason.trim()}
                onClick={() => act('decline', () => decline(id, declineReason.trim()))}
              >
                {t('detail.confirmDecline', 'Send decline')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {!live && (
        <Card className="mt-4">
          <div className="text-sm text-muted-strong">
            {t('detail.closed', 'This referral is closed ({{status}}).', {
              status: t(`status.${referral.status}`, referral.status),
            })}
            {referral.declineReason && <div className="mt-1 text-xs text-muted">{referral.declineReason}</div>}
          </div>
        </Card>
      )}
    </Layout>
  );
};

export default ReferralDetailPage;
